/**
 * PrescriptionDocumentsPage (Phase-2, Module 5)
 *
 * Multi-source prescription upload (Doctor / Officer / Staff / Coordinator),
 * list, preview/download, and version history. Distinct from the existing
 * doctor-only Prescription module — additive, no Phase-1 change.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  IconButton, Tooltip, Chip, Stack, Typography, Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MedicationLiquid as RxIcon, Upload as UploadIcon, Visibility as ViewIcon, History as HistoryIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload, ConfirmDialog, OfficerSelector } from '../../components/common';
import { PrescriptionDocumentApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import type { PrescriptionDocument } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const PrescriptionDocumentsPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<PrescriptionDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patientName: '', doctorName: '', hospitalName: '',
    prescriptionDate: new Date().toISOString().slice(0, 10), validUntil: '', notes: '',
  });

  const [detail, setDetail] = useState<PrescriptionDocument | null>(null);
  const [versions, setVersions] = useState<PrescriptionDocument[]>([]);
  const [verOpen, setVerOpen] = useState(false);
  const [verFile, setVerFile] = useState<File | null>(null);
  const [verTitle, setVerTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [delId, setDelId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try { setRows((await PrescriptionDocumentApi.listByOfficer(officerId)) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load prescriptions'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!officerId || !file) return;
    setSaving(true); setError(null);
    try {
      await PrescriptionDocumentApi.upload(file, {
        officerId, patientName: form.patientName || undefined, doctorName: form.doctorName || undefined,
        hospitalName: form.hospitalName || undefined, prescriptionDate: form.prescriptionDate || undefined,
        validUntil: form.validUntil || undefined, notes: form.notes || undefined,
      });
      setOpen(false); setFile(null);
      setForm({ patientName: '', doctorName: '', hospitalName: '', prescriptionDate: new Date().toISOString().slice(0, 10), validUntil: '', notes: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Upload failed'); }
    finally { setSaving(false); }
  };

  const openVersions = async (row: PrescriptionDocument) => {
    setDetail(row);
    try { setVersions((await PrescriptionDocumentApi.getVersions(row.id)) || []); }
    catch { setVersions([]); }
    setVerOpen(true);
  };

  const handleAddVersion = async () => {
    if (!detail || !verFile) return;
    setUploading(true); setError(null);
    try {
      await PrescriptionDocumentApi.createVersion(detail.id, verFile, { documentTitle: verTitle || undefined });
      setVersions((await PrescriptionDocumentApi.getVersions(detail.id)) || []);
      setVerFile(null); setVerTitle(''); load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Version upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (delId == null) return;
    try {
      await PrescriptionDocumentApi.delete(delId);
      setDelId(null);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Delete failed');
      setDelId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'prescriptionNumber', headerName: 'Rx No.', width: 150 },
    { field: 'doctorName', headerName: 'Doctor', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'hospitalName', headerName: 'Hospital', flex: 1.2, minWidth: 150, renderCell: (p) => p.value || '—' },
    { field: 'prescriptionDate', headerName: 'Date', width: 120 },
    { field: 'uploadSource', headerName: 'Source', width: 130, renderCell: (p) => <Chip size="small" label={p.row.uploadSourceDisplayName || p.value} variant="outlined" /> },
    { field: 'version', headerName: 'Ver', width: 70, renderCell: (p) => `v${p.value ?? 1}` },
    {
      field: 'actions', headerName: 'Actions', width: 170, sortable: false,
      renderCell: (p) => {
        const url = toAbsoluteFileUrl(p.row.fileUrl);
        return (
          <Box>
            {url && <Tooltip title="View"><IconButton size="small" href={url} target="_blank" rel="noopener noreferrer"><ViewIcon fontSize="small" /></IconButton></Tooltip>}
            <Tooltip title="Versions"><IconButton size="small" onClick={() => openVersions(p.row)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDelId(p.row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Prescription Documents" subtitle="Upload & track prescriptions (multi-source)" icon={<RxIcon />}
      actionLabel={officerId ? 'Upload Prescription' : undefined} actionIcon={<UploadIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Prescription</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Patient Name" value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Doctor Name" value={form.doctorName} onChange={(e) => setForm({ ...form, doctorName: e.target.value })} fullWidth />
              <TextField label="Hospital" value={form.hospitalName} onChange={(e) => setForm({ ...form, hospitalName: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField type="date" label="Prescription Date" InputLabelProps={{ shrink: true }} value={form.prescriptionDate} onChange={(e) => setForm({ ...form, prescriptionDate: e.target.value })} fullWidth />
              <TextField type="date" label="Valid Until" InputLabelProps={{ shrink: true }} value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} fullWidth />
            </Box>
            <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
            <FileUpload onFileSelected={setFile} uploading={saving} label="Select prescription file (PDF / image)" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!officerId || !file || saving} sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verOpen} onClose={() => setVerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Version History — {detail?.prescriptionNumber}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Add New Version</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Version title (optional)" value={verTitle} onChange={(e) => setVerTitle(e.target.value)} sx={{ flex: 1 }} />
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>File<input type="file" hidden onChange={(e) => setVerFile(e.target.files?.[0] || null)} /></Button>
                <Button variant="contained" onClick={handleAddVersion} disabled={!verFile || uploading}>{uploading ? '...' : 'Add'}</Button>
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={1}>
              {versions.map((v) => {
                const url = toAbsoluteFileUrl(v.fileUrl);
                return (
                  <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2">v{v.version} — {v.doctorName || '—'} @ {v.hospitalName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{v.uploadDate || v.prescriptionDate} · {v.uploadedByName}</Typography>
                    </Box>
                    {url && <Button size="small" href={url} target="_blank" rel="noopener noreferrer">View</Button>}
                  </Box>
                );
              })}
              {versions.length === 0 && <Typography variant="body2" color="text.secondary">No previous versions.</Typography>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setVerOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <ConfirmDialog
        open={delId != null}
        title="Delete prescription document?"
        message="This will permanently remove the prescription and its file. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDelId(null)}
      />
    </PageScaffold>
  );
};

export default PrescriptionDocumentsPage;